import { Parse } from '@/store/parseUtils'
import { callApi, mapObjectIdField } from '@/store/apiUtils'
import type { Study, API } from '@pocketprep/types'
import type { ClientMockExam, ClientExam, ClientBundle } from '@/store/bundle/state'
import { bundleModule } from '@/store/bundle/module'
import { fetchLoadable } from '@/store/utils'

/** @todo temporary workaround until we fully migrate server-nest */
const mapSunsetAtField = <T extends { sunsetAt?: string }>(
    obj: T
): Omit<T, 'sunsetAt'> & { sunsetAt?: { iso: string } } => {
    const { sunsetAt, ...rest } = obj
    if (typeof sunsetAt === 'string') {
        return { ...rest, sunsetAt: { iso: sunsetAt } }
    }
    return rest
}

const fetchBundlesResponse: {
    bundles?: ClientBundle[]
    exams?: ClientExam[]
    mockExams?: ClientMockExam[]
} = {}

const fetchBundles = async () => {
    await fetchLoadable(bundleModule.state.bundles, async () => {
        const nestApiDomain = import.meta.env.VUE_APP_NEST_API_URL_DOMAIN
        if (!nestApiDomain) {
            const bundles = await new Parse.Query<Study.Class.Bundle>('Bundle')
                .equalTo('isDisabled', false)
                .findAll()
            return bundles.map(b => b.toJSON())
        }

        const response: API.FetchBundlesResponse = await callApi({
            method: 'GET',
            url: '/bundles',
        })

        /** Map of mockExam IDs to exam objects for quick lookup */
        const mockExamsMappedById = new Map<string, ClientMockExam>()

        /** MockExams with objectId for backwards compatibility */
        response.mockExams.forEach((mockExam) => {
            const mappedMockExam = mapObjectIdField(mockExam)
            mockExamsMappedById.set(mappedMockExam.objectId, mappedMockExam)
        })

        /** Map of exam IDs to exam objects for quick lookup */
        const examsMappedById = new Map<string, ClientExam>()

        /** Exams with objectId and hydrated mockExams list for backwards compatibility */
        response.examMetadatas.forEach((exam) => {
            const hydratedMockExams = exam.mockExamIds.reduce<ClientMockExam[]>(
                (accumulator, mockExamId) => {
                    const mockExam = mockExamsMappedById.get(mockExamId)
                    if (mockExam) {
                        accumulator.push(mockExam)
                    }
                    return accumulator
                }, [])
            const mappedExam = mapObjectIdField({ ...exam, mockExams: hydratedMockExams })
            examsMappedById.set(mappedExam.objectId, mapSunsetAtField(mappedExam))
        })

        /** Bundles with objectId and hydrated exams list for backwards compatibility */
        const bundles: ClientBundle[] = response.bundles.map(bundle => {
            const hydratedExams = bundle.examMetadataIds.reduce<ClientExam[]>((accumulator, examId) => {
                const exam = examsMappedById.get(examId)
                if (exam) {
                    accumulator.push(exam)
                }
                return accumulator
            }, [])

            return {
                ...mapObjectIdField(bundle),
                exams: hydratedExams,
            }
        })

        fetchBundlesResponse.exams = Array.from(examsMappedById.values())
        fetchBundlesResponse.mockExams = Array.from(mockExamsMappedById.values())

        return bundles
    })

    return fetchBundlesResponse
}

export default {
    fetchBundles,
}
